import React, { useEffect, useRef, useState } from 'react';
import MonsterImage from '../components/MonsterImage';
import { getMonsterImgPath } from '../utils/monster';
import { BattleFrame, Element, BUFF_NAMES } from './simTypes';
import { TacticBuild } from './TacticBuild';

/** 시뮬레이션 탭(자동·직접 조작)이 같이 쓰는 것 */

/** 서버(`/api/sim/units`)가 주는 몹 하나 */

/**
 * 공격 게이지 바 — 0~100% 는 하늘색, 100% 를 넘은 만큼은 진한 파랑으로 그 위에 겹친다 (게임과 같게, 사용자 2026-10-21 "초과분 게이지가 안보여").
 * 초과분은 최대 100%(바 한 칸)까지만 그린다.
 */
function AtbBar({ atb, height, back, fill, over, ms }: { atb: number; height: number; back: string; fill: string; over: string; ms: number }) {
    const a = Math.max(0, atb || 0);
    return (
        <div style={{ height, background: back, position: 'relative' }}>
            <div style={{ width: `${Math.min(100, a)}%`, height: '100%', background: fill, transition: `width ${ms / 1000}s` }} />
            <div style={{ position: 'absolute', left: 0, top: 0, width: `${Math.min(100, Math.max(0, a - 100))}%`, height: '100%', background: over, transition: `width ${ms / 1000}s` }} />
        </div>
    );
}

export interface SimUnit {
    name: string;
    attribute: Element;
    found: boolean;
    archetype?: string | null;
    base?: { hp: number; atk: number; def: number; spd: number; critRate: number; critDamage: number; resistance: number; accuracy: number };
    leader?: any;
    unitMasterId?: number | null;
    /** 화면용 스킬 — 공식·효과는 서버에만 있다 */
    skills?: Array<{ id: number; slot: number; name: string; cooltime: number | null; passive: boolean }>;
}

/** 고친 능력치 한 벌 */
export interface Stats { hp: number; atk: number; def: number; spd: number; critRate: number; critDamage: number; resistance: number; accuracy: number }

/** 고른 칸 */
export interface Slot {
    pick: { name: string; attribute: Element; image_path?: string; displayName?: string } | null;
    unit: SimUnit | null;
    /** 룬을 안 적었을 때 쓰는 기본 능력치(실전 중앙값) */
    stats: Stats | null;
    sets: number[];
    /** 룬 설정(공략 탭과 같은 모양) — 적었거나 전투기록에서 불러왔으면 이걸로 싸운다 */
    build?: TacticBuild | null;
    /** 아티팩트 부옵 합(효과 번호 → 값) — 전투기록에서 불러왔을 때만 */
    art?: Record<number, number>;
    /** 아티팩트 부옵을 둘로 — [속성, 타입] (시뮬 [룬 변경]에서 적은 것. 엔진에는 합 `art` 를 보낸다) */
    arts?: [Array<[number, number]>, Array<[number, number]>];
    /** 유물 고유 효과 `[종류, 기준값, %]` — 전투기록에서 불러온 것(서버 엔진이 쓴다) */
    relics?: Array<[number, number, number]>;
    /** 점령전 방덱 선 스킬 칸(0부터) — 전투기록의 `defense_skill_option` 에서. 방덱 몹 하나에만(서버 엔진이 그 몹의 첫 턴에 쓴다) */
    opener?: number | null;
    /** 낀 룬·아티팩트·유물 원본(SWEX `unit_list[]` 한 칸 모양) — [학습 기록]에서 불러온 칸만. 룬을 고치면 지운다 (시뮬 몹 줄 아래 BuildRunes 가 룬 별·상세를 그린다) */
    raw?: any;
}

/** 효과 이름 → 짧은 한국어 */
export const FX_KO: Record<string, string> = {
    'Increase ATK': '공증', 'Increase DEF': '방증', 'Increase ATK SPD': '속증', 'Increase CRI Rate': '치확증',
    'Immunity': '면역', 'Shield': '보호막', 'Endure': '버팀', 'Invincible': '무적', 'Counter': '반격', 'Threat': '끌기',
    'Stone': '석상 (받는 피해↓·행동 불가 면역, 다음 자기 턴까지)',
    'Recovery': '지속회복', 'Defend': '보호', 'Increase CRI Resist': '치저증', 'Increase Damage': '피해증가',
    'Decrease DEF': '방깎', 'Decrease ATK': '공깎', 'Decrease ATK SPD': '속감', 'Brand': '낙인', 'Stun': '기절',
    'Freeze': '빙결', 'Sleep': '수면', 'Provoke': '도발', 'Silence': '침묵', 'Oblivion': '망각', 'Unrecoverable': '회복불가',
    'Continuous DMG': '지속피해', 'Increase Glancing Hit Chance': '빗맞음', 'Beneficial Effects Blocked': '버프차단',
    'Cannot Critical Hit': '치명불가', 'Remove Buff': '버프제거', 'Cleanse': '해제', 'Heal': '회복', 'Self-Heal': '자기회복',
    'Increase ATB': '게이지+', 'Decrease ATB': '게이지-', 'Absorb ATB': '게이지흡수', 'Increase Cooltime': '쿨+',
    'Reduce Cooltime': '쿨-', 'Steal Buff': '버프뺏기', 'Ally Attack': '협공', 'Additional Turn': '추가턴',
    'Additional Attack': '추가공격', 'Ignore DEF': '방무', 'Guaranteed Critical Hit': '확정치명', 'Revive': '부활',
    'Debuff Bonus Damage': '디버프비례', 'Ignore Resistance': '저항무시', 'Elemental Advantage': '속성유리'
};
export const fxKo = (n: string) => FX_KO[n] || n;

/** 엔진 이름(`Mermaid(Wind)`) → 화면 이름(`풍머메`) */
export function nameMap(slots: Slot[], monsterMapping: any): (n: string) => string {
    const m = new Map<string, string>();
    for (const s of slots) if (s.pick) m.set(`${s.pick.name}(${s.pick.attribute})`, getMonsterImgPath(monsterMapping, s.pick).displayName);
    return (n: string) => m.get(n) || n;
}

/**
 * **효과 아이콘에 넣을 글자** — 게임 아이콘 그림은 저장소에 없어서 게임처럼 작은 색 네모에 한두 글자를 넣는다.
 * 강화(이로운 효과)는 파랑·초록 계열, 약화(해로운 효과)는 빨강 계열. 없는 효과는 한국어 이름 앞 두 글자.
 */
const FX_GLYPH: Record<string, string> = {
    'Increase ATK': '공↑', 'Increase DEF': '방↑', 'Increase ATK SPD': '속↑', 'Increase CRI Rate': '치↑', 'Increase CRI Resist': '치저',
    'Immunity': '면역', 'Shield': '보호', 'Endure': '버팀', 'Invincible': '무적', 'Counter': '반격', 'Threat': '끌기', 'Stone': '석상',
    'Recovery': '회복', 'Defend': '수호', 'Increase Damage': '피↑', 'Reflect DMG': '반사',
    'Decrease DEF': '방↓', 'Decrease ATK': '공↓', 'Decrease ATK SPD': '속↓', 'Brand': '낙인', 'Stun': '기절',
    'Freeze': '빙결', 'Sleep': '수면', 'Provoke': '도발', 'Silence': '침묵', 'Oblivion': '망각', 'Unrecoverable': '회불',
    'Continuous DMG': '지속', 'Increase Glancing Hit Chance': '빗맞', 'Beneficial Effects Blocked': '강불',
    'Harmful Effect Removal Blocked': '해불', 'Bomb': '폭탄', 'Mark': '표식'
};
/** 강화 몇 가지는 게임처럼 색을 달리한다 (공격=빨강 테두리 파랑, 방어=초록 …) */
const BUFF_TINT: Record<string, string> = {
    'Increase ATK': '#1e88e5', 'Increase DEF': '#43a047', 'Increase ATK SPD': '#00acc1', 'Increase CRI Rate': '#8e24aa',
    'Immunity': '#fbc02d', 'Shield': '#90a4ae', 'Invincible': '#ffb300', 'Endure': '#6d4c41', 'Counter': '#5e35b1', 'Recovery': '#2e7d32'
};

/**
 * **효과 아이콘 그림** — 게임 아이콘(SWARFARM 효과 표의 `icon_filename`, `public/img/effects/` 에 받아 둠, 2026-10-11).
 * 그림이 없는 효과만 색 네모에 글자로 그린다.
 */
const FX_ICON: Record<string, string> = {
    'Increase ATK': 'buff_attack_up.png', 'Increase DEF': 'buff_defence_up.png', 'Increase CRI Rate': 'buff_crit_up.png',
    'Increase CRI Resist': 'buff_crit_down.png', 'Increase ATK SPD': 'buff_speed.png', 'Recovery': 'buff_heal.png',
    'Counter': 'buff_counter.png', 'Immunity': 'buff_immune.png', 'Invincible': 'buff_invinciblity.png', 'Reflect DMG': 'buff_reflect.png',
    'Shield': 'buff_shield.png', 'Endure': 'buff_endure.png', 'Defend': 'buff_protect.png', 'Soul Protection': 'buff_soul_protect.png',
    'Threat': 'buff_threat.png', 'Vampire': 'buff_vampire.png', 'Berserk': 'buff_berserk.png', 'Stealth': 'buff_stealth.png',
    'Increase Glancing Hit Chance': 'debuff_glancing_hit.png', 'Decrease ATK': 'debuff_attack_down.png', 'Decrease DEF': 'debuff_defence_down.png',
    'Decrease ATK SPD': 'debuff_slow.png', 'Beneficial Effects Blocked': 'debuff_block_buffs.png', 'Bomb': 'debuff_bomb.png',
    'Provoke': 'debuff_provoke.png', 'Sleep': 'debuff_sleep.png', 'Continuous DMG': 'debuff_dot.png', 'Freeze': 'debuff_freeze.png',
    'Stun': 'debuff_stun.png', 'Unrecoverable': 'debuff_block_heal.png', 'Silence': 'debuff_silence.png', 'Brand': 'debuff_brand.png',
    'Oblivion': 'debuff_oblivious.png', 'Harmful Effect Removal Blocked': 'debuff_cleanse_block.png', 'Suppression': 'debuff_suppress.png',
    'Seal': 'debuff_seal.png', 'Unrevivable': 'debuff_unrevivable.png', 'Mark': 'debuff_mark.png'
};

/** 효과 하나 — 게임 아이콘(없으면 색 네모 + 글자), 오른쪽 아래에 남은 턴 */
export function FxIcon({ name, turns, size = 22 }: { name: string; turns?: number; size?: number }) {
    const buff = BUFF_NAMES.has(name) ? true : undefined;
    const bg = buff ? (BUFF_TINT[name] || '#1976d2') : '#c62828';
    const glyph = FX_GLYPH[name] || fxKo(name).slice(0, 2);
    const icon = FX_ICON[name];
    return (
        <span title={`${fxKo(name)}${turns ? ` ${turns}턴` : ''}`} style={{
            position: 'relative', width: size, height: size, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: icon ? 'transparent' : `linear-gradient(160deg, ${bg} 0%, ${bg}cc 100%)`,
            border: icon ? 'none' : `1.5px solid ${buff ? 'rgba(255,255,255,0.75)' : '#ffcdd2'}`,
            boxShadow: '0 1px 2px rgba(0,0,0,0.35)', color: '#fff', fontSize: Math.round((glyph.length > 1 ? 8.5 : 11) * size / 22 * 10) / 10, fontWeight: 900, letterSpacing: -0.5, lineHeight: 1
        }}>
            {icon ? <img src={`/img/effects/${icon}`} alt={fxKo(name)} width={size} height={size} style={{ display: 'block', borderRadius: 4 }} draggable={false} /> : glyph}
            {turns !== undefined && turns > 0 && (
                <span style={{
                    position: 'absolute', right: -4, bottom: -4, minWidth: size < 20 ? 9 : 11, height: size < 20 ? 9 : 11, borderRadius: 6, background: '#212121',
                    color: '#fff', fontSize: size < 20 ? 7 : 8, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px'
                }}>{turns}</span>
            )}
        </span>
    );
}

/** 몹 위에 떠오르는 글자 하나 — 피해·회복·치명타 */
export interface FloatText { text: string; color: string; big?: boolean; /** 치명타 — 글자 대신 숫자 앞에 터지는 아이콘 */ crit?: boolean }

/** 스킬 아이콘 주소 — 서버가 SWARFARM 에서 처음 한 번 받아 둔다 (`GET /api/sim/skill-icon/:id`) */
export const skillIconUrl = (id: number) => `/api/sim/skill-icon/${id}`;

/** 이미 미리 받기 시작한 아이콘 — 같은 걸 또 부르지 않고, Image 를 잡아 둬 브라우저가 받다 말고 버리지 않게 한다 */
const preloadedIcons = new Map<number, HTMLImageElement>();
/**
 * **스킬 아이콘 미리 받기** — 시뮬레이션에 들어갈 몹들의 스킬 아이콘을 재생·직접 플레이 전에 받아 둔다
 * (사용자 2026-10-16: 재생 중에 스킬이 뜰 때 받느라 늦게 보였다). 서버가 1년 캐시를 주므로 한 번 받으면 다시 안 받는다.
 */
export function preloadSkillIcons(units: Array<SimUnit | null | undefined>) {
    for (const u of units) {
        for (const k of u?.skills || []) {
            if (!k?.id || preloadedIcons.has(k.id)) continue;
            const img = new Image();
            img.decoding = 'async';
            img.src = skillIconUrl(k.id);
            preloadedIcons.set(k.id, img);
        }
    }
}

/**
 * **로그의 스킬 이름 → 그 몹의 스킬 번호.** 로그 이름엔 "(반격)"·"(추가 공격)"·"(패시브 협공)" 같은 꼬리가 붙어 오니 한글 괄호는 떼고 본다.
 * 행동 불가·지속 피해·패시브 같은 줄은 스킬이 아니라 null.
 */
export function skillIdOf(slot: Slot | undefined, logSkill: string): number | null {
    return skillOf(slot, logSkill)?.id ?? null;
}
/** 로그의 스킬 → 그 몹 스킬의 번호와 칸 */
export function skillOf(slot: Slot | undefined, logSkill: string): { id: number; slot: number } | null {
    if (!slot?.unit?.skills || !logSkill || logSkill.startsWith('(')) return null;
    const base = logSkill.replace(/\([^)]*[\uac00-\ud7a3][^)]*\)/g, '').trim();
    const k = slot.unit.skills.find((x) => !x.passive && x.name === base);
    return k ? { id: k.id, slot: k.slot } : null;
}
/**
 * **화면에 보이는 스킬 이름 — "1스·2스·3스"** (한글 스킬명이 없어서, 사용자 지시 2026-10-11). 꼬리("(반격)" 같은 한글)는 붙여 둔다.
 * 행동 불가·지속 피해·패시브 줄은 그대로.
 */
export function skillLabel(slot: Slot | undefined, logSkill: string, note = ''): string {
    // 엔진 특수 줄 — 괄호 이름을 화면 말로 (사용자 2026-10-22: 영어로 된 것 다 한글로)
    if (logSkill === '(저항)') return '저항';
    if (logSkill === '(패시브)') return '회복';
    if (logSkill === '(패시브 발동)') {
        const n = passiveSlotOf(slot, note);
        return n ? `패시브 ${n}스 발동` : '패시브 발동';
    }
    const k = skillOf(slot, logSkill);
    if (!k) {
        // 한글이 아닌 스킬 이름은 그대로 두지 않는다 — 몇 번째 스킬인지 모르면 '스킬'. 괄호 줄('(행동 불가)' 따위)은 괄호만 벗긴다
        if (logSkill.startsWith('(')) return logSkill.replace(/^\((.*)\)$/, '$1');
        return /[\uac00-\ud7a3]/.test(logSkill) ? logSkill : '스킬';
    }
    const tail = (logSkill.match(/\([^)]*[\uac00-\ud7a3][^)]*\)/g) || []).join('');
    return `${k.slot}스${tail}`;
}

/** 패시브 이름(엔진, 영어 — 'Sand Blade (Passive)') → 그 몹의 몇 번째 스킬 */
function passiveSlotOf(slot: Slot | undefined, note: string): number | null {
    if (!slot?.unit?.skills || !note) return null;
    const bare = (x: string) => x.replace(/\s*\(passive\)\s*$/i, '').trim().toLowerCase();
    const k = slot.unit.skills.find((x) => x.name === note || bare(x.name) === bare(note));
    return k ? k.slot : null;
}

/** 저항한 효과 — 엔진 효과 이름(remove_buff 따위) → 화면 말 */
const RESIST_KO: Record<string, string> = {
    remove_buff: '강화 해제', continuous_dmg: '지속 피해', stun: '기절', sleep: '수면', freeze: '빙결',
    decrease_atk_spd: '공속 감소', decrease_def: '방어 감소', decrease_atk: '공격 감소', decrease_atb: '공게 감소', removed_atb: '공게 감소',
    decrease_crit_rate: '치확 감소', brand: '낙인', block_beneficial_effects: '강화 불가', silence: '침묵', seal: '봉인',
    oblivion: '망각', provoke: '도발', threat: '도발', bomb: '폭탄', steal_buff: '강화 훔치기', unrecoverable: '회복 불가',
    increase_cooltime: '재사용 증가', increase_debuff_duration: '약화 연장', hp_reduction: '체력 감소', transfer_debuff: '약화 옮기기',
    spread_debuff: '약화 퍼뜨리기', spread_debuffs: '약화 퍼뜨리기', turn_to_stone: '석화', shadow_tie: '그림자 묶기',
    anti_revive: '부활 불가', glancing: '빗맞음', start_cooltime: '재사용 시작', reflect_harmful: '약화 반사'
};

/**
 * **기록 줄 꼬리(note)를 화면 말로** (사용자 2026-10-22) — 저항한 효과는 한글 이름, 패시브 이름(영어)은 지운다(앞에 'N스 발동' 으로
 * 이미 적었다), 회복 '+12204' 는 천 단위로. 그 밖에 영어 낱말만 있으면 효과 이름표(FX_KO)로, 그래도 영어면 지운다.
 */
export function noteLabel(logSkill: string, note: string): string {
    if (!note) return '';
    if (logSkill === '(저항)') return RESIST_KO[note] || (fxKo(note) !== note ? fxKo(note) : '해로운 효과');
    if (logSkill === '(패시브 발동)') return '';
    const n = note.match(/^\+(\d+)$/);
    if (n) return `+${Number(n[1]).toLocaleString()}`;
    if (/[\uac00-\ud7a3]/.test(note)) return note;
    const ko = fxKo(note);
    if (ko !== note) return ko;
    return RESIST_KO[note] || '';
}
/** 엔진 이름(`Mermaid(Wind)`) → 칸 번호 0~5 (같은 이름이 양쪽에 있으면 `prefer` 순서로) */
export function slotIndexOf(slots: Slot[], name: string, prefer: number[] = [0, 1, 2, 3, 4, 5]): number {
    return prefer.find((i) => slots[i]?.pick && `${slots[i].pick!.name}(${slots[i].pick!.attribute})` === name) ?? -1;
}

/**
 * **한 장면(로그 줄 묶음)에서 몹마다 띄울 글자** — 대상에게 피해(타격마다), 지속 피해·패시브 회복은 그 몹에게, 치명타는 크게.
 * 같은 이름이 양쪽에 있으면 행동한 몹의 반대편을 먼저 본다. 재생 창·직접 해보기가 같이 쓴다.
 */
export function floatsOf(lines: Array<{ actor: string; skill: string; target: string; damage: number; crit: boolean; glancing: boolean; note: string; hits?: number[] }>, slots: Slot[]): FloatText[][] {
    const out: FloatText[][] = [[], [], [], [], [], []];
    for (const l of lines) {
        const actor = slotIndexOf(slots, l.actor);
        const enemyFirst = actor >= 0 && actor < 3 ? [3, 4, 5, 0, 1, 2] : [0, 1, 2, 3, 4, 5];
        // 폭주·스킬 추가 턴 — 그 몹 위에 "턴 획득"
        if (l.skill === '(추가 턴)' || l.skill === '(폭주 추가턴)') { if (actor >= 0) out[actor].unshift({ text: '턴 획득', color: '#7fe3ff', big: true }); continue; }
        if (l.skill === '(지속 피해)' && l.damage) { if (actor >= 0) out[actor].push({ text: l.damage.toLocaleString(), color: '#ff8a80' }); continue; }
        // 해로운 효과를 저항·면역으로 막음 — 막은 몹 위에 글자 (사용자 2026-10-20). actor = 막은 몹
        if (l.skill === '(저항)') { if (actor >= 0) out[actor].push({ text: '저항', color: '#ffffff' }); continue; }
        if (l.skill === '(면역)') { if (actor >= 0) out[actor].push({ text: '면역', color: '#ffe082' }); continue; }
        // 폭탄 폭발 피해 — 터진 몹 위에 (actor = 폭탄을 단 몹) · 부활 — 살아난 몹 위에 (사용자 2026-10-20)
        if (l.skill === '(폭탄)' && l.damage) { if (actor >= 0) out[actor].push({ text: l.damage.toLocaleString(), color: '#ff7043', big: true }); continue; }
        if (l.skill === '(부활)') { if (actor >= 0) out[actor].unshift({ text: '부활', color: '#7cfc5a', big: true }); continue; }
        if (l.skill === '(패시브)' || l.skill === '(회복)') {
            const m = /\+(\d+)/.exec(l.note || '');
            if (m && actor >= 0) out[actor].push({ text: `+${Number(m[1]).toLocaleString()}`, color: '#7cfc5a' });
            continue;
        }
        if (!l.damage || !l.target || l.target === '(아군)') continue;
        const t = slotIndexOf(slots, l.target, enemyFirst);
        if (t < 0) continue;
        const hits = l.hits && l.hits.length > 1 ? l.hits.map((h) => Math.round(h)) : [l.damage];
        // 피해는 빨강(사용자 지시), 치명타는 크게 + 터지는 아이콘(글자 "치명타" 는 안 쓴다), 빗맞음은 회색
        for (const h of hits) out[t].push(l.crit ? { text: h.toLocaleString(), color: '#ff3b30', big: true, crit: true } : { text: h.toLocaleString(), color: l.glancing ? '#cfd8dc' : '#ff5a4f' });
        if (!l.crit && l.glancing) out[t].push({ text: '빗맞음', color: '#cfd8dc' });
    }
    return out;
}

/**
 * **타격 연출 시간(초)** — 달려가기 · 한 타 · 돌아오기 · 다음 몹 사이 (사용자 2026-10-21 "타수마다 공격하는 모션 — 1타면 가서 흔들리고 돌아옴, 4타면 4번").
 */
export const STRIKE = { go: 0.25, hit: 0.3, back: 0.25, gap: 0.05 };
/**
 * **광역 훑기 시간(초)** — 적을 둘 이상 맞히면 한 마리씩 들러 치는 대신 적 앞을 빠르게 훑고 지나간다
 * (사용자 2026-10-21 "C 차례로 훑기 — 대신 좀 빠르게, 한 대씩 치고 간다기보다 훑고 가는 느낌"). 다타 광역은 오가며 타 수만큼 훑는다.
 */
export const SWEEP = { go: 0.18, step: 0.12, linger: 0.08, back: 0.2 };
/**
 * 때린 몹 — 달려갈 첫 적 · 들르는 적 차례(광역이면 훑는 길, 다타면 오가며 되풀이) · 차례 · 출발 때(초) · 타수 · 길이(초) · 광역 훑기인지
 */
export type Lunge = { to: number; tos: number[]; order: number; start: number; n: number; dur: number; sweep: boolean } | null;
/** 맞은 몹마다 맞는 때(초)와 그 타의 피해 */
export type HitAt = { at: number; dmg: number };
export interface StrikePlan { lunges: Lunge[]; hits: HitAt[][]; total: number }

/**
 * **한 장면의 타격 계획** — 때린 몹마다(주인공 먼저, 협공·반격은 그 뒤 차례로) 대상 앞까지 달려가 **타수만큼** 치고 돌아온다.
 * 타마다 대상이 흔들리고 체력이 그 타의 피해만큼 깎인다(`BattleField` prevUnits). 광역은 맞은 몹 모두가 같은 때 맞는다.
 * 타의 피해는 로그 hits(보호막 전 한 타씩)를 그 줄의 피해 합에 맞춰 나눈다. 지속 피해·폭탄은 바로(0초).
 */
export function strikesOf(lines: Array<{ actor: string; skill: string; target: string; damage: number; hits?: number[] }>, slots: Slot[], actor = -1): StrikePlan {
    const lunges: Lunge[] = [null, null, null, null, null, null];
    const hits: HitAt[][] = [[], [], [], [], [], []];
    const name = (k: number) => slots[k]?.pick && `${slots[k].pick!.name}(${slots[k].pick!.attribute})`;
    const order: Array<{ a: number; to: number; perT: Map<number, number[]> }> = [];
    for (const l of lines) {
        if (!l.damage) continue;
        const a = actor >= 0 && name(actor) === l.actor ? actor : slotIndexOf(slots, l.actor);
        if (a < 0) continue;
        if (l.skill === '(지속 피해)' || l.skill === '(폭탄)') { hits[a].push({ at: 0, dmg: l.damage }); continue; }
        if (!l.target || l.target === '(아군)' || /^\(/.test(l.skill)) continue;
        const t = slotIndexOf(slots, l.target, a < 3 ? [3, 4, 5, 0, 1, 2] : [0, 1, 2, 3, 4, 5]);
        if (t < 0) continue;
        let e = order.find((x) => x.a === a);
        if (!e) { e = { a, to: -1, perT: new Map() }; order.push(e); }
        if (e.to < 0 && (t < 3) !== (a < 3)) e.to = t;
        const raw = l.hits && l.hits.length > 1 ? l.hits : [l.damage];
        const sum = raw.reduce((x, y) => x + y, 0) || 1;
        const list = e.perT.get(t) ?? e.perT.set(t, []).get(t)!;
        for (const h of raw) list.push(h * l.damage / sum);
    }
    // 주인공이 맨 앞
    const mi = order.findIndex((x) => x.a === actor);
    if (mi > 0) order.unshift(...order.splice(mi, 1));
    let clock = 0;
    order.forEach((e, k) => {
        const n = Math.max(1, ...[...e.perT.values()].map((x) => x.length));
        // 맞힌 적 — 화면 왼쪽부터
        const foes = [...e.perT.keys()].filter((t) => (t < 3) !== (e.a < 3)).sort((x, y) => FIELD_POS[x].x - FIELD_POS[y].x);
        if (foes.length >= 2) {
            // 광역 훑기 — 타마다 한 번 훑는다(짝수 번째는 되돌아오며). 들를 때 그 적이 그 타를 맞는다
            const path: number[] = [];
            for (let r = 0; r < n; r++) path.push(...(r % 2 ? [...foes].reverse() : foes));
            const seen = new Map<number, number>();
            path.forEach((t, j) => {
                const r = seen.get(t) ?? 0;
                seen.set(t, r + 1);
                const dmg = e.perT.get(t)?.[r];
                if (dmg !== undefined) hits[t].push({ at: clock + SWEEP.go + j * SWEEP.step + 0.03, dmg });
            });
            // 적이 아닌 대상(아군 쪽에 맞은 것)은 첫 훑기 때
            for (const [t, list] of e.perT) if (!foes.includes(t)) list.forEach((dmg) => hits[t].push({ at: clock + SWEEP.go, dmg }));
            const dur = SWEEP.go + (path.length - 1) * SWEEP.step + SWEEP.linger + SWEEP.back;
            lunges[e.a] = { to: foes[0], tos: path, order: k, start: clock, n, dur, sweep: true };
            clock += dur + STRIKE.gap;
            return;
        }
        for (const [t, list] of e.perT) list.forEach((dmg, i) => hits[t].push({ at: clock + STRIKE.go + i * STRIKE.hit + STRIKE.hit * 0.3, dmg }));
        const dur = STRIKE.go + n * STRIKE.hit + STRIKE.back;
        if (e.to >= 0) lunges[e.a] = { to: e.to, tos: [e.to], order: k, start: clock, n, dur, sweep: false };
        clock += dur + STRIKE.gap;
    });
    return { lunges, hits, total: clock };
}

/**
 * 광역 훑기 — 들르는 적 앞(대상 쪽 75%)을 차례로 빠르게 지나가며(지날 때 살짝 크게) 끝나면 돌아온다. 몹마다 자리가 달라 몹마다 만든다
 */
function sweepKeyframes(name: string, pts: Array<{ x: number; y: number }>, dur: number): string {
    const pc = (t: number) => `${Math.min(100, (t / dur) * 100).toFixed(2)}%`;
    const ks = ['0% { transform: translate(0, 0); }'];
    pts.forEach((q, j) => {
        const t = SWEEP.go + j * SWEEP.step;
        ks.push(`${pc(t)} { transform: translate(${q.x}px, ${q.y}px) scale(1.12); }`);
    });
    const last = pts[pts.length - 1];
    ks.push(`${pc(SWEEP.go + (pts.length - 1) * SWEEP.step + SWEEP.linger)} { transform: translate(${last.x}px, ${last.y}px) scale(1.05); }`);
    ks.push('100% { transform: translate(0, 0); }');
    return `@keyframes ${name} { ${ks.join(' ')} }`;
}

/** 타수 n 의 달려가기 — 닿으면 타마다 한 번씩 앞으로 내찌르고(흔들림) 돌아온다. 길이 = 달려가기 + n 타 + 돌아오기 */
function lungeKeyframes(n: number): string {
    const D = STRIKE.go + n * STRIKE.hit + STRIKE.back;
    const pc = (t: number) => `${(t / D * 100).toFixed(2)}%`;
    const at = 'transform: translate(var(--dx), var(--dy)) scale(1.08);';
    const push = 'transform: translate(calc(var(--dx) * 1.1), calc(var(--dy) * 1.1)) scale(1.14);';
    const ks = [`0% { transform: translate(0, 0); }`, `${pc(STRIKE.go)} { ${at} }`];
    for (let i = 0; i < n; i++) {
        const t0 = STRIKE.go + i * STRIKE.hit;
        ks.push(`${pc(t0 + STRIKE.hit * 0.3)} { ${push} }`, `${pc(t0 + STRIKE.hit * 0.75)} { ${at} }`);
    }
    ks.push(`${pc(STRIKE.go + n * STRIKE.hit)} { ${at} }`, `100% { transform: translate(0, 0); }`);
    return `@keyframes simLunge${n} { ${ks.join(' ')} }`;
}

/** 이번 장면에 패시브가 발동한 몹 — 엔진 로그 `(패시브 발동)` 줄(note = 패시브 이름)에서. 아이콘 + "N스" */
export function passivesOf(lines: Array<{ actor: string; skill: string; note: string }>, slots: Slot[]): Array<{ id: number; name: string } | null> {
    const out: Array<{ id: number; name: string } | null> = [null, null, null, null, null, null];
    for (const l of lines) {
        if (l.skill !== '(패시브 발동)') continue;
        const i = slotIndexOf(slots, l.actor);
        if (i < 0 || out[i]) continue;
        // 패시브는 skillOf(액티브만 찾는다)로 못 찾아 아이콘이 한 번도 안 떴다 (사용자 2026-10-22 "물발 패시브가 동작돼도 안뜨는데") — 패시브까지 이름으로
        const k = slots[i]?.unit?.skills?.find((x) => x.name === l.note.trim());
        if (k) out[i] = { id: k.id, name: `${k.slot}스` };
    }
    return out;
}

/** 한 장면에서 몹마다 처음 쓴 스킬 (반격·협공 포함) — 이미지에 아이콘으로 */
export function usedSkillsOf(lines: Array<{ actor: string; skill: string }>, slots: Slot[], actor = -1): Array<{ id: number; name: string } | null> {
    const out: Array<{ id: number; name: string } | null> = [null, null, null, null, null, null];
    const order = actor >= 0 ? [actor, 0, 1, 2, 3, 4, 5] : [0, 1, 2, 3, 4, 5];
    for (const l of lines) {
        const i = order.find((k) => slots[k]?.pick && `${slots[k].pick!.name}(${slots[k].pick!.attribute})` === l.actor && skillOf(slots[k], l.skill));
        if (i === undefined || out[i]) continue;
        out[i] = { id: skillOf(slots[i], l.skill)!.id, name: skillLabel(slots[i], l.skill) };
    }
    return out;
}

/** 떠오르는 글자 움직임 — 한 번만 넣는다 */
const FLOAT_CSS = `@keyframes simSkillPop { 0% { opacity: 0; transform: scale(0.4); } 25% { opacity: 1; transform: scale(1.15); } 40% { transform: scale(1); } 85% { opacity: 1; } 100% { opacity: 0.85; } }
@keyframes simFloatUp { 0% { opacity: 0; transform: translate(-50%, 6px) scale(0.85); } 12% { opacity: 1; transform: translate(-50%, 0) scale(1.08); } 70% { opacity: 1; } 100% { opacity: 0; transform: translate(-50%, -26px) scale(1); } }`;

/** 몹 한 장 — 이미지·이름·체력(+보호막)·공격 게이지·걸린 효과(아이콘), 맞은 피해는 이미지 위에 떠오른다 */
export function UnitCard({ slot, u, monsterMapping, acting, selectable, chosen, onClick, footer, floats, floatKey, skill }: {
    slot: Slot; u: BattleFrame['units'][number]; monsterMapping: any;
    acting?: boolean; selectable?: boolean; chosen?: boolean; onClick?: () => void; footer?: React.ReactNode;
    /** 이번 장면에 이 몹 위로 띄울 글자 (`floatKey` 가 바뀌면 다시 떠오른다) */
    floats?: FloatText[]; floatKey?: string | number;
    /** 이번 장면에 이 몹이 쓴 스킬 — 이미지 왼쪽 위에 아이콘으로 */
    skill?: { id: number; name: string } | null;
}) {
    const img = slot.pick ? getMonsterImgPath(monsterMapping, slot.pick) : null;
    const size = 58;
    const hpPct = u.maxHp ? Math.max(0, Math.min(100, (u.hp / u.maxHp) * 100)) : 0;
    const shPct = u.maxHp ? Math.min(100 - hpPct, (u.shield / u.maxHp) * 100) : 0;
    const border = chosen ? '2px solid #e53935' : acting ? '2px solid #ffb300' : selectable ? '2px dashed #e57373' : '2px solid transparent';
    return (
        <div onClick={selectable ? onClick : undefined} style={{
            flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '8px 6px',
            borderRadius: 12, border, cursor: selectable ? 'pointer' : 'default',
            background: chosen ? 'rgba(229,57,53,0.08)' : acting ? 'rgba(255, 193, 7, 0.18)' : 'transparent'
        }}>
            <style>{FLOAT_CSS}</style>
            {/* 강화·약화 효과 — 몹 이미지 위 (게임처럼 강화 먼저) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', alignItems: 'flex-end', minHeight: 24, width: '100%' }}>
                {u.fx.map((n, k) => ({ n, t: u.fxTurns?.[k] })).sort((a, b) => Number(BUFF_NAMES.has(b.n)) - Number(BUFF_NAMES.has(a.n)))
                    .map(({ n, t }, k) => <FxIcon key={`${n}${k}`} name={n} turns={t} />)}
            </div>
            <div style={{ position: 'relative', filter: u.dead ? 'grayscale(1)' : 'none', opacity: u.dead ? 0.35 : 1 }}>
                {img?.imgPath && <MonsterImage imgPath={img.imgPath} alt={img.displayName} style={{ width: size, height: size, objectFit: 'contain', borderRadius: 10 }} />}
                {/* 이번 장면에 쓴 스킬 — 이미지 왼쪽 위에 튀어나온다 */}
                {skill && (
                    <img
                        key={`${floatKey}-${skill.id}`} src={skillIconUrl(skill.id)} alt={skill.name} title={skill.name} width={30} height={30} draggable={false}
                        style={{
                            position: 'absolute', left: -12, top: -10, zIndex: 3, borderRadius: 7, border: '2px solid #ffd54f',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.45)', background: '#222', animation: 'simSkillPop 0.9s ease-out both'
                        }}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                )}
                {/* 맞은 피해·회복 — 이미지 위로 떠오른다 */}
                {floats && floats.length > 0 && (
                    <div key={floatKey} style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 0 }}>
                        {floats.slice(0, 4).map((f, k) => (
                            <span key={k} style={{
                                position: 'absolute', left: '50%', top: 8 + k * 15, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 2,
                                fontSize: f.big ? 17 : 14, fontWeight: 900, color: f.color, letterSpacing: -0.3,
                                textShadow: '0 0 3px #fff, 0 1px 2px rgba(0,0,0,0.55), 1px 0 0 #000, -1px 0 0 #000, 0 1px 0 #000, 0 -1px 0 #000',
                                animation: `simFloatUp 1.1s ease-out ${k * 0.12}s both`
                            }}>{f.text}</span>
                        ))}
                    </div>
                )}
            </div>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#444', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img?.displayName}</span>
            {/* 체력바(+보호막)와 공격 게이지(파랑)를 붙인다 — 체력 숫자는 없다 */}
            <div style={{ width: '100%', borderRadius: 4, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.35)', background: '#2b2b2b', boxSizing: 'border-box' }}>
                <div style={{ height: 8, display: 'flex' }}>
                    <div style={{ width: `${hpPct}%`, background: hpPct > 50 ? '#43a047' : hpPct > 25 ? '#fb8c00' : '#e53935', transition: 'width 0.3s' }} />
                    <div style={{ width: `${shPct}%`, background: '#e0e0e0', transition: 'width 0.3s' }} />
                </div>
                <AtbBar atb={u.dead ? 0 : u.atb} height={4} back="#1a1a1a" fill="#29b6f6" over="#1546b8" ms={300} />
            </div>
            {footer}
        </div>
    );
}

/** 속성 아이콘 (SWARFARM 속성 그림, `public/img/sim/elem_*.png`) */
const ELEM_ICON: Record<string, string> = { Fire: 'fire', Water: 'water', Wind: 'wind', Light: 'light', Dark: 'dark' };

/** 전장의 자리 — 게임 전투 화면처럼 방덱은 위(조금 작게), 공덱은 아래(크게). 왼쪽부터 0·1·2 */
const FIELD_POS: Array<{ x: number; y: number; scale: number }> = [
    { x: 20, y: 84, scale: 1 }, { x: 50, y: 90, scale: 1 }, { x: 80, y: 84, scale: 1 },
    { x: 27, y: 40, scale: 0.82 }, { x: 50, y: 37, scale: 0.82 }, { x: 73, y: 40, scale: 0.82 }
];

/** 떠오르는 피해 숫자 테두리 — 검은 그림자 여덟 겹 + 아래 그늘 */
const NUM_OUTLINE = [[-1.5, 0], [1.5, 0], [0, -1.5], [0, 1.5], [-1.2, -1.2], [1.2, -1.2], [-1.2, 1.2], [1.2, 1.2]]
    .map(([x, y]) => `${x}px ${y}px 0 #000`).join(', ') + ', 0 2px 4px rgba(0,0,0,0.7)';

const FIELD_CSS = `@keyframes simArrowBob { 0% { transform: translateY(0); } 50% { transform: translateY(6px); } 100% { transform: translateY(0); } }
@keyframes simCircleSpin { from { transform: translate(-50%, -50%) rotateX(68deg) rotate(0deg); } to { transform: translate(-50%, -50%) rotateX(68deg) rotate(360deg); } }
@keyframes simCirclePulse { 0% { opacity: 0.8; } 50% { opacity: 1; } 100% { opacity: 0.8; } }
@keyframes simRingSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes simBeam { 0% { opacity: 0.35; transform: translateX(-50%) scaleY(0.8); } 50% { opacity: 1; transform: translateX(-50%) scaleY(1); } 100% { opacity: 0.35; transform: translateX(-50%) scaleY(0.8); } }
@keyframes simTwinkle { 0% { opacity: 0; transform: translate(-50%, -50%) scale(0.3); } 40% { opacity: 1; transform: translate(-50%, -50%) scale(1); } 80% { opacity: 0; transform: translate(-50%, -50%) scale(0.4); } 100% { opacity: 0; } }
@keyframes simSkillPop { 0% { opacity: 0; transform: scale(0.4); } 25% { opacity: 1; transform: scale(1.15); } 40% { transform: scale(1); } 85% { opacity: 1; } 100% { opacity: 0.85; } }
@keyframes simPassivePop { 0% { opacity: 0; transform: scale(0.4); } 15% { opacity: 1; transform: scale(1.15); } 25% { transform: scale(1); } 80% { opacity: 1; } 100% { opacity: 0; transform: scale(0.9); } }
@keyframes simHitShake { 0% { transform: translate(0, 0); } 15% { transform: translate(-7px, 2px); } 30% { transform: translate(6px, -2px); } 45% { transform: translate(-4px, 1px); } 60% { transform: translate(3px, 0); } 80% { transform: translate(-1px, 0); } 100% { transform: translate(0, 0); } }
@keyframes simHitFlash { 0% { filter: brightness(1); } 12% { filter: brightness(2.6) saturate(0.2); } 35% { filter: brightness(1.3) sepia(0.6) hue-rotate(-30deg) saturate(3); } 100% { filter: brightness(1); } }
@keyframes simHitSlash { 0% { opacity: 0; transform: translate(-50%, -50%) rotate(-35deg) scaleX(0.1); } 20% { opacity: 1; transform: translate(-50%, -50%) rotate(-35deg) scaleX(1.1); } 100% { opacity: 0; transform: translate(-50%, -50%) rotate(-35deg) scaleX(1.3); } }
@keyframes simStarOrbit { 0% { transform: translate(calc(-50% - var(--r)), -50%) scale(0.85); opacity: 1; } 25% { transform: translate(-50%, calc(-50% + var(--ry))) scale(1.15); opacity: 1; } 50% { transform: translate(calc(-50% + var(--r)), -50%) scale(0.85); opacity: 1; } 75% { transform: translate(-50%, calc(-50% - var(--ry))) scale(0.6); opacity: 0.55; } 100% { transform: translate(calc(-50% - var(--r)), -50%) scale(0.85); opacity: 1; } }
@keyframes simLunge { 0% { transform: translate(0, 0); } 30% { transform: translate(var(--dx), var(--dy)) scale(1.08); } 55% { transform: translate(var(--dx), var(--dy)) scale(1.08); } 100% { transform: translate(0, 0); } }
@keyframes simRing { 0% { opacity: 0.55; transform: translate(-50%, -50%) scale(0.92); } 50% { opacity: 1; transform: translate(-50%, -50%) scale(1.05); } 100% { opacity: 0.55; transform: translate(-50%, -50%) scale(0.92); } }
@keyframes simFieldFloat { 0% { opacity: 0; transform: translate(-50%, 8px) scale(0.7); } 12% { opacity: 1; transform: translate(-50%, 0) scale(1.15); } 22% { transform: translate(-50%, 0) scale(1); } 75% { opacity: 1; } 100% { opacity: 0; transform: translate(-50%, -30px) scale(1); } }`;

/**
 * **게임 전투 화면 같은 전장** — 경기장 배경 위에 몹 여섯. 몹마다 머리 위에 효과 아이콘(남은 턴), 그 아래 금테 바(속성 아이콘 +
 * 초록 체력 + 파란 공격 게이지, 보호막은 체력 뒤 흰색). 지금 턴인 몹은 발밑이 노랗게 빛나고, 고를 수 있는 몹은 빨간 고리.
 * 맞은 피해·회복·치명타는 그 몹 위로 떠오르고, 이번에 쓴 스킬 아이콘이 몹 옆에 뜬다. 재생 창·직접 해보기가 같이 쓴다
 * (사용자 지시 2026-10-11: 게임 화면 참고, 배경 넣기).
 */
export function BattleField({ slots, units, monsterMapping, acting = -1, floats, floatKey, used, selectable, onPick, height = 540, aimed = -1, strikes, prevUnits, rate = 1, passives }: {
    slots: Slot[]; units: BattleFrame['units']; monsterMapping: any; acting?: number;
    floats?: FloatText[][]; floatKey?: string | number; used?: Array<{ id: number; name: string } | null>;
    selectable?: (i: number) => boolean; onPick?: (i: number) => void; height?: number;
    /** 지금 겨냥된 대상(빨간 화살표) — 마우스를 올린 몹이 먼저, 없으면 이것(AI 추천 대상) */
    aimed?: number;
    /** 이번 장면의 타격 — 때린 몹은 대상 앞까지 달려가 타수만큼 치고 돌아오고, 맞은 몹은 타마다 흔들린다 (`strikesOf`) */
    strikes?: StrikePlan;
    /** 이번 장면 앞의 몹 상태 — 있으면 체력·보호막이 타마다 깎인다(없으면 한 번에) */
    prevUnits?: BattleFrame['units'];
    /** 재생 빠르기 — 연출 시간을 이만큼 줄인다 */
    rate?: number;
    /** 이번 장면에 발동한 패시브 — 몹 오른쪽 위에 아이콘과 "N스" (`passivesOf`) */
    passives?: Array<{ id: number; name: string } | null>;
}) {
    const posOf = (i: number) => FIELD_POS[i];
    const [hovered, setHovered] = useState(-1);
    // 달려갈 거리는 픽셀로 — 전장 너비를 잰다
    const box = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(860);
    useEffect(() => {
        const el = box.current;
        if (!el) return;
        const ro = new ResizeObserver(() => setWidth(el.clientWidth || 860));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const red = hovered >= 0 && selectable?.(hovered) ? hovered : aimed;
    /** 좁은 전장(모바일)은 몹·체력 줄을 폭에 맞춰 줄인다 — 720px 이상은 그대로, 가장 작게 62% (사용자 2026-10-22 "몹 크기 좀 줄여") */
    const fit = Math.max(0.62, Math.min(1, width / 720));
    // 타마다 체력 — 장면이 바뀐 때부터 지난 시간으로 맞은 타를 센다(맞는 때마다 다시 그린다)
    const lunges = strikes?.lunges, hits = strikes?.hits;
    // 시작 때는 그리는 그 순간에 맞춘다 — effect 에서 맞추면 새 장면 첫 그림이 앞 장면 시계로 그려져 다 맞은 체력이 잠깐 보였다가
    // 앞 장면 체력으로 다시 차는 일이 있었다 (사용자 2026-10-21 "체력이 닳았다가 다시 찼다가 다시 닳는데")
    const t0 = useRef(0);
    const keyAt = useRef<unknown>(undefined);
    if (keyAt.current !== floatKey) { keyAt.current = floatKey; t0.current = performance.now(); }
    const [, setTick] = useState(0);
    useEffect(() => {
        const times = [...new Set((hits || []).flat().map((h) => h.at))];
        const ts = times.map((at) => window.setTimeout(() => setTick((x) => x + 1), (at / rate) * 1000 + 20));
        return () => ts.forEach((x) => window.clearTimeout(x));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [floatKey]);
    const elapsed = t0.current ? ((performance.now() - t0.current) / 1000) * rate : Infinity;
    const lungeCss = [...new Set((lunges || []).filter((x) => x && !x.sweep).map((x) => x!.n))].map(lungeKeyframes).join('\n');
    // 광역 훑기 — 몹마다 지나갈 자리(픽셀)로
    const sweepCss = (lunges || []).map((lg, i) => {
        if (!lg?.sweep) return '';
        const me = FIELD_POS[i];
        const pts = lg.tos.map((t) => ({ x: Math.round((FIELD_POS[t].x - me.x) / 100 * width * 0.75), y: Math.round((FIELD_POS[t].y - me.y) / 100 * height * 0.75) }));
        return sweepKeyframes(`simSweep${i}`, pts, lg.dur);
    }).join('\n');
    return (
        <div ref={box} style={{
            position: 'relative', width: '100%', height, borderRadius: 14, overflow: 'hidden', userSelect: 'none',
            background: '#1c2430 url(/img/sim/arena.jpg) center 60% / cover no-repeat', boxShadow: 'inset 0 0 60px rgba(0,0,0,0.55)'
        }}>
            <style>{FIELD_CSS}</style>
            {(lungeCss || sweepCss) && <style>{lungeCss + '\n' + sweepCss}</style>}
            {[3, 4, 5, 0, 1, 2].map((i) => {
                const slot = slots[i], fin = units[i];
                if (!slot?.pick || !fin) return null;
                // 맞는 중이면 앞 장면 체력·보호막에서 이미 맞은 타만큼 깎은 값 (보호막 먼저) — 다 맞으면 이 장면 값
                const myHits = hits?.[i] || [];
                const pv = prevUnits?.[i];
                // 이 장면에 회복·보호막으로 오히려 늘었으면 타마다 깎지 않는다(깎였다가 다시 차 보인다)
                const landedAll = !myHits.length || !pv || pv.hp + pv.shield <= fin.hp + fin.shield || myHits.every((h) => h.at <= elapsed);
                const u = landedAll ? fin : (() => {
                    const L = myHits.filter((h) => h.at <= elapsed).reduce((x, h) => x + h.dmg, 0);
                    // 끝 체력 아래로는 내려가지 않는다 — 넘친 피해·그 장면 안 회복으로 끝에 다시 차 보이지 않게
                    return { ...fin, dead: false, shield: Math.max(0, pv.shield - L), hp: Math.max(1, fin.hp, pv.hp - Math.max(0, L - pv.shield)) };
                })();
                const p = posOf(i);
                const img = getMonsterImgPath(monsterMapping, slot.pick);
                const size = Math.round(84 * p.scale * fit);
                const barW = Math.round(118 * p.scale * fit);
                const hpPct = u.maxHp ? Math.max(0, Math.min(100, (u.hp / u.maxHp) * 100)) : 0;
                const shPct = u.maxHp ? Math.min(100 - hpPct, (u.shield / u.maxHp) * 100) : 0;
                const pick = !!selectable?.(i);
                const fx = u.fx.map((n, k) => ({ n, t: u.fxTurns?.[k] })).sort((a, b) => Number(BUFF_NAMES.has(b.n)) - Number(BUFF_NAMES.has(a.n)));
                const elem = ELEM_ICON[slot.pick.attribute] || 'fire';
                const sk = used?.[i];
                const ps = passives?.[i];
                /** 행동 불능(기절·수면·빙결) — 머리 위에 별이 돌고 몹이 어두워진다 */
                const incap = !u.dead && u.fx.some((n) => n === 'Stun' || n === 'Sleep' || n === 'Freeze');
                /** 달려가기 — 대상 바로 앞(대상 쪽으로 75%)까지 가서 타수만큼 내찌르고 온다. 협공·반격은 앞 몹이 돌아온 뒤.
                 *  광역(적 둘 이상)은 적 앞을 차례로 빠르게 훑고 온다 (`strikesOf` · sweepCss) */
                const lg = lunges?.[i] || null;
                const off = (t: number) => { const q = posOf(t); return { x: Math.round((q.x - p.x) / 100 * width * 0.75), y: Math.round((q.y - p.y) / 100 * height * 0.75) }; };
                const lungeStyle: React.CSSProperties = !lg ? {} : lg.sweep
                    ? { animation: `simSweep${i} ${lg.dur / rate}s linear ${lg.start / rate}s both` }
                    : ({
                        '--dx': `${off(lg.to).x}px`, '--dy': `${off(lg.to).y}px`,
                        animation: `simLunge${lg.n} ${lg.dur / rate}s ease-in-out ${lg.start / rate}s both`
                    } as any);
                /** 맞는 연출 — 타마다 흔들림·번쩍임 (지속 피해·폭탄도) */
                const hitAnim = myHits.length ? myHits.map((h) => `simHitShake ${0.26 / rate}s ease-out ${h.at / rate}s, simHitFlash ${0.3 / rate}s ease-out ${h.at / rate}s`).join(', ') : undefined;
                const head = (
                    <>
                        {/* 효과 아이콘 — 바 위 */}
                        <div style={{ display: 'flex', gap: 2, minHeight: Math.round(22 * fit), marginBottom: 2, justifyContent: 'center', maxWidth: barW + 30, flexWrap: 'wrap' }}>
                            {fx.map(({ n, t }, k) => <FxIcon key={`${n}${k}`} name={n} turns={t} size={Math.round(22 * fit)} />)}
                        </div>
                        {/* 속성 + 체력 + 공격 게이지 (붙인 바, 금테) */}
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6, opacity: u.dead ? 0.5 : 1 }}>
                            <img src={`/img/sim/elem_${elem}.png`} alt={slot.pick.attribute} width={26 * p.scale * fit} height={26 * p.scale * fit} draggable={false}
                                style={{ borderRadius: '50%', border: '2px solid #c9a24a', background: '#222', zIndex: 1, marginRight: -6, boxShadow: '0 1px 3px rgba(0,0,0,0.6)' }} />
                            <div style={{ width: barW, border: '2px solid #c9a24a', borderLeft: 'none', borderRadius: '0 6px 6px 0', overflow: 'hidden', background: '#111', boxShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                                <div style={{ height: Math.max(6, Math.round(9 * p.scale)), display: 'flex', background: '#3a3a3a' }}>
                                    <div style={{ width: `${hpPct}%`, background: 'linear-gradient(180deg, #7ee35f 0%, #3fae2a 100%)', transition: 'width 0.35s' }} />
                                    <div style={{ width: `${shPct}%`, background: '#f2f2f2', transition: 'width 0.35s' }} />
                                </div>
                                <AtbBar atb={u.dead ? 0 : u.atb} height={Math.max(3, Math.round(4 * p.scale))} back="#0b1622"
                                    fill="linear-gradient(180deg, #6fd3ff 0%, #1e9be0 100%)" over="linear-gradient(180deg, #3d7bff 0%, #1236a8 100%)" ms={350} />
                            </div>
                        </div>
                    </>
                );
                return (
                    <React.Fragment key={i}>
                    <div
                        onClick={pick && onPick ? () => onPick(i) : undefined}
                        onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered((h) => (h === i ? -1 : h))}
                        style={{
                            position: 'absolute', left: `${p.x}%`, top: `${p.y}%`, transform: 'translate(-50%, -100%)',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: pick ? 'pointer' : 'default', zIndex: lg ? 7 : i < 3 ? 3 : 2
                        }}
                    >
                        {/* 고를 수 있는 몹 — 게임처럼 머리 위 화살표 (겨냥된 몹은 빨강, 나머지는 노랑) */}
                        {pick && <TargetArrow red={red === i} scale={p.scale} />}
                        {/* 효과 아이콘·체력바 — 자리만 잡는다(보이는 것은 위 층 — 달려온 몹이 가리지 않게) */}
                        <div style={{ visibility: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>{head}</div>
                        {/* 몹 — 발밑 빛(지금 턴)·조준 고리(고를 수 있음) */}
                        <div style={{ position: 'relative', width: size, height: size }}>
                            {acting === i && !u.dead && <TurnCircle size={size} />}
                            {img.imgPath && (
                                <div key={`lg-${floatKey}`} style={{ position: 'relative', width: size, height: size, zIndex: 1, ...lungeStyle }}>
                                {/* 맞으면 — 흔들리고 번쩍이고 베는 빛이 지나간다 */}
                                <div key={`hit-${floatKey}`} style={{
                                    position: 'relative', width: size, height: size,
                                    animation: hitAnim
                                }}>
                                <MonsterImage imgPath={img.imgPath} alt={img.displayName} style={{
                                    position: 'relative', width: size, height: size, objectFit: 'contain', borderRadius: 14,
                                    filter: u.dead ? 'grayscale(1) brightness(0.6)' : incap ? 'brightness(0.5) drop-shadow(0 6px 6px rgba(0,0,0,0.55))' : 'drop-shadow(0 6px 6px rgba(0,0,0,0.55))', opacity: u.dead ? 0.45 : 1
                                }} />
                                {myHits.map((h, k) => (
                                    <div key={k} style={{
                                        position: 'absolute', left: '50%', top: '50%', width: size * 1.3, height: Math.max(4, size * 0.08), borderRadius: 999, pointerEvents: 'none',
                                        background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, #fff 45%, #ffe9a8 55%, rgba(255,255,255,0) 100%)',
                                        boxShadow: '0 0 12px rgba(255,240,200,0.9)', animation: `simHitSlash ${0.3 / rate}s ease-out ${h.at / rate}s both`
                                    }} />
                                ))}
                                </div>
                                </div>
                            )}
                            {incap && (
                                <div style={{ position: 'absolute', left: '50%', top: -6, width: 0, height: 0, zIndex: 4, pointerEvents: 'none' }}>
                                    {[0, 1, 2].map((k) => (
                                        <span key={k} style={{
                                            position: 'absolute', left: 0, top: 0, fontSize: Math.round(16 * p.scale), color: '#ffe14d', textShadow: '0 0 4px #ffb300, 0 1px 2px #000',
                                            ['--r' as any]: `${Math.round(size * 0.32)}px`, ['--ry' as any]: `${Math.round(size * 0.09)}px`,
                                            animation: `simStarOrbit 1.2s linear ${-k * 0.4}s infinite`
                                        }}>★</span>
                                    ))}
                                </div>
                            )}
                            {ps && (
                                <div key={`${floatKey}-p${ps.id}`} style={{ position: 'absolute', right: -20 * p.scale, top: -10, zIndex: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', animation: `simPassivePop ${1.1 / rate}s ease-out both`, pointerEvents: 'none' }}>
                                    <img src={skillIconUrl(ps.id)} alt={ps.name} title={`패시브 ${ps.name}`} width={30 * p.scale * fit} height={30 * p.scale * fit} draggable={false}
                                        style={{ borderRadius: 7, border: '2px solid #b388ff', boxShadow: '0 0 8px rgba(179,136,255,0.8)', background: '#222' }}
                                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                                    <span style={{ fontSize: 11, fontWeight: 900, color: '#e1d4ff', textShadow: '0 1px 2px #000', marginTop: 1 }}>{ps.name}</span>
                                </div>
                            )}
                            {sk && (
                                <img key={`${floatKey}-${sk.id}`} src={skillIconUrl(sk.id)} alt={sk.name} title={sk.name} width={34 * p.scale * fit} height={34 * p.scale * fit} draggable={false}
                                    style={{ position: 'absolute', left: -18 * p.scale, top: -8, zIndex: 4, borderRadius: 7, border: '2px solid #ffd54f', boxShadow: '0 2px 6px rgba(0,0,0,0.6)', background: '#222', animation: `simPassivePop ${1.1 / rate}s ease-out both` }}
                                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                            )}
                        </div>
                    </div>
                    {/* 위 층 — 효과 아이콘·체력바·떠오르는 글자. 달려온 몹(zIndex 7)보다 위 (사용자 2026-10-21 "몹이 체력바를 가리는 경우") */}
                    <div style={{
                        position: 'absolute', left: `${p.x}%`, top: `${p.y}%`, transform: 'translate(-50%, -100%)', pointerEvents: 'none',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 8
                    }}>
                        {pick && <div style={{ visibility: 'hidden' }}><TargetArrow red={false} scale={p.scale} /></div>}
                        {/* 아이콘 풍선 도움말·눌러 고르기는 그대로 되게 이 부분만 마우스를 받는다 */}
                        <div
                            onClick={pick && onPick ? () => onPick(i) : undefined}
                            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered((h) => (h === i ? -1 : h))}
                            style={{ pointerEvents: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: pick ? 'pointer' : 'default' }}
                        >{head}</div>
                        <div style={{ position: 'relative', width: size, height: size }}>
                            {/* 떠오르는 글자 — 게임처럼 외곽선 굵은 글자 */}
                            {floats?.[i]?.length ? (
                                <div key={floatKey} style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 0, zIndex: 5 }}>
                                    {floats[i].slice(0, 6).map((f, k, all) => (
                                        <span key={k} style={{
                                            position: 'absolute', left: '50%', top: size * 0.15 + k * 18, whiteSpace: 'nowrap', pointerEvents: 'none',
                                            // 테두리는 그림자 여덟 겹(text-stroke 는 굵은 숫자 안쪽을 파먹어 깨져 보였다 — 사용자 2026-10-22), 굵기 800
                                            fontSize: Math.round((f.big ? 24 : 18) * Math.max(0.8, fit)), fontWeight: 800, color: f.color, letterSpacing: -0.3,
                                            textShadow: NUM_OUTLINE,
                                            // 피해 숫자는 그 타가 맞는 때에 (앞에서부터 타 차례) — 나머지는 차례로
                                            animation: `simFieldFloat ${1.2 / rate}s ease-out ${myHits[k] ? myHits[k].at / rate : k * 0.13}s both`
                                        }}>{/* 치명타 글자는 그 몹의 첫 치명타 숫자 위에만 — 줄마다 띄우면 위 숫자를 가린다 */}
                                            {f.crit && all.findIndex((x) => x.crit) === k && <CritIcon size={f.big ? 26 : 20} above />}{f.text}</span>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
}

/**
 * **대상 화살표** — 게임처럼 머리 위에 까딱이는 굵은 아래 화살표. 겨냥된 대상은 빨강, 고를 수 있는 나머지는 노랑.
 */
function TargetArrow({ red, scale = 1 }: { red: boolean; scale?: number }) {
    const w = Math.round(54 * scale), h = Math.round(51 * scale);
    const top = red ? '#ff7a59' : '#ffe27a', bottom = red ? '#d8261b' : '#f2a900', edge = red ? '#7a0d06' : '#8a5a00';
    return (
        <svg width={w} height={h} viewBox="0 0 40 38" style={{ display: 'block', marginBottom: 1, animation: 'simArrowBob 0.8s ease-in-out infinite', filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.55))', pointerEvents: 'none' }}>
            <defs>
                <linearGradient id={red ? 'simArrowR' : 'simArrowY'} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={top} />
                    <stop offset="100%" stopColor={bottom} />
                </linearGradient>
            </defs>
            <path d="M12 2 H28 V17 H37 L20 36 L3 17 H12 Z" fill={`url(#${red ? 'simArrowR' : 'simArrowY'})`} stroke={edge} strokeWidth="2" strokeLinejoin="round" />
            <path d="M15 5 H25 V9 H15 Z" fill="rgba(255,255,255,0.45)" />
        </svg>
    );
}

/**
 * **지금 턴을 잡은 몹 발밑 마법진** — 게임 화면 그대로(사용자 지시 2026-10-11): 바닥에 눕힌 민트빛 마법진. 굵은 바깥 고리가 토막토막 돌고,
 * 가는 고리·안쪽 고리가 반대로 돌며, 앞뒤좌우에 화살 표식. 양옆에 빛기둥이 솟고 반짝이가 깜빡인다.
 */
function TurnCircle({ size }: { size: number }) {
    const d = Math.round(size * 1.9);
    const spin = (sec: number, rev = false): React.CSSProperties => ({ transformOrigin: '100px 100px', animation: `simRingSpin ${sec}s linear infinite${rev ? ' reverse' : ''}` });
    const chevron = (deg: number) => (
        <path key={deg} d="M91,170 L100,183 L109,170 L100,175 Z" fill="#d9ffdf" stroke="#7dff8f" strokeWidth="1.2" transform={`rotate(${deg} 100 100)`} />
    );
    return (
        <div style={{ position: 'absolute', left: '50%', top: '92%', width: 0, height: 0, pointerEvents: 'none' }}>
            {/* 바닥 마법진 — 정사각 그림을 납작하게 눌러 바닥에 눕힌다 */}
            <div style={{ position: 'absolute', left: 0, top: 0, width: d, height: d, transform: 'translate(-50%, -50%) scaleY(0.36)', animation: 'simCirclePulse 1.6s ease-in-out infinite' }}>
                <svg viewBox="0 0 200 200" width={d} height={d} style={{ overflow: 'visible', display: 'block' }}>
                    <defs>
                        <filter id="simGlow" x="-30%" y="-30%" width="160%" height="160%">
                            <feGaussianBlur stdDeviation="3.2" result="b" />
                            <feMerge><feMergeNode in="b" /><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                        </filter>
                        <radialGradient id="simFloor" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stopColor="rgba(150,255,160,0.10)" />
                            <stop offset="70%" stopColor="rgba(120,255,140,0.22)" />
                            <stop offset="100%" stopColor="rgba(120,255,140,0)" />
                        </radialGradient>
                    </defs>
                    <circle cx="100" cy="100" r="98" fill="url(#simFloor)" />
                    <g filter="url(#simGlow)">
                        {/* 바깥 굵은 고리 — 토막 */}
                        <g style={spin(16)}>
                            <circle cx="100" cy="100" r="88" fill="none" stroke="#b9ffbf" strokeWidth="9" strokeDasharray="74 18" strokeLinecap="round" />
                        </g>
                        {/* 가장 바깥 가는 선 */}
                        <g style={spin(22, true)}>
                            <circle cx="100" cy="100" r="97" fill="none" stroke="rgba(170,255,185,0.75)" strokeWidth="1.6" strokeDasharray="34 9" />
                        </g>
                        {/* 안쪽 고리 */}
                        <g style={spin(10, true)}>
                            <circle cx="100" cy="100" r="66" fill="none" stroke="#d2ffd8" strokeWidth="3.4" strokeDasharray="52 20" strokeLinecap="round" />
                            <circle cx="100" cy="100" r="58" fill="none" stroke="rgba(190,255,200,0.6)" strokeWidth="1.2" />
                        </g>
                        {/* 화살 표식 — 앞(아래)·뒤·좌·우 */}
                        {[0, 90, 180, 270].map(chevron)}
                    </g>
                </svg>
            </div>
            {/* 양옆 빛기둥 */}
            {[-1, 1].map((sd) => (
                <div key={sd} style={{
                    position: 'absolute', left: sd * d * 0.4, bottom: 0, width: Math.max(4, size * 0.07), height: size * 0.95, transform: 'translateX(-50%)', transformOrigin: 'bottom center',
                    background: 'linear-gradient(0deg, rgba(225,255,232,0.95) 0%, rgba(170,255,190,0.55) 45%, rgba(170,255,190,0) 100%)',
                    filter: 'blur(1.2px)', borderRadius: 4, animation: `simBeam ${1.1 + (sd > 0 ? 0.3 : 0)}s ease-in-out infinite`
                }} />
            ))}
            {/* 반짝이 */}
            {[[-0.46, -0.55, 0], [0.44, -0.7, 0.5], [0.3, -0.25, 0.9], [-0.28, -0.85, 1.3]].map(([x, y, dl], k) => (
                <svg key={k} viewBox="0 0 20 20" width={size * 0.16} height={size * 0.16} style={{
                    position: 'absolute', left: x * d, top: y * size, transform: 'translate(-50%, -50%)', overflow: 'visible',
                    animation: `simTwinkle 1.4s ease-in-out ${dl}s infinite`
                }}>
                    <path d="M10,0 L12,8 L20,10 L12,12 L10,20 L8,12 L0,10 L8,8 Z" fill="#f2fff4" style={{ filter: 'drop-shadow(0 0 3px #9dff9f)' }} />
                </svg>
            ))}
        </div>
    );
}

/** **치명타 아이콘** — 노랑·주황 별 모양 터짐(숫자 앞). 재생 기록 줄에도 작게 쓴다 */
/**
 * **치명타 표시 — 게임처럼 노란 '치명타' 글자** (사용자 2026-10-22: 터지는 별 그림은 싼티 난다 · 게임 다시보기에서 잘라 둔
 * swSim/replay-tools/labels/crit.png 의 모양 — 노란 글자에 진한 갈색 테두리). `above` 면 숫자 바로 위에 띄우고(전장),
 * 아니면 글 줄 안에 작게(행동 기록). size 는 예전 별 그림 크기 값을 그대로 받아 글자 크기로 쓴다.
 */
export function CritIcon({ size = 22, above = false }: { size?: number; above?: boolean }) {
    // 테두리는 text-stroke 대신 그림자 여덟 겹 — stroke 는 작은 한글 획 안쪽을 파먹어 깨져 보였다(사용자 2026-10-22).
    // 굵기 900 은 글꼴에 없어 브라우저가 억지로 두껍게 해 뭉개졌다 → 800
    const o = above ? 1.5 : 1;
    const edge = '#5a2200';
    const outline = [[-o, 0], [o, 0], [0, -o], [0, o], [-o, -o], [o, -o], [-o, o], [o, o]].map(([x, y]) => `${x}px ${y}px 0 ${edge}`).join(', ');
    const style: React.CSSProperties = {
        fontSize: Math.round(size * (above ? 0.62 : 0.72)), fontWeight: 800, color: '#ffe14d', letterSpacing: 0, lineHeight: 1,
        WebkitTextStroke: '0', textShadow: `${outline}${above ? ', 0 2px 4px rgba(0,0,0,0.6)' : ''}`, whiteSpace: 'nowrap',
        WebkitFontSmoothing: 'antialiased'
    };
    if (above) {
        return <span aria-label="치명타" style={{ ...style, position: 'absolute', left: '50%', bottom: '92%', transform: 'translateX(-50%)' }}>치명타</span>;
    }
    return <span aria-label="치명타" style={{ ...style, display: 'inline-block', marginRight: 3, verticalAlign: '1px' }}>치명타</span>;
}
